import { useEffect, useState, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import generatePdfBlob from './InvoicePdfExporter';
import InvoicePdfTemplate from './InvoicePdfTemplate';

export default function InvoicePdfPreview({ 
  show, 
  onClose, 
  elementId = 'invoice-pdf-root', 
  invoiceNo = 'invoice',
  invoiceTitle = '',
  useTemplate = false,
  invoiceData = {} 
}) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState('');
  const [error, setError] = useState('');
  const templateRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    if (!show) return () => { mounted = false; };
    setLoading(true);
    setError('');
    setBlobUrl('');

    (async () => {
      try {
        const id = useTemplate ? (templateRef.current?.id || 'invoice-template-root') : elementId;
        const blob = await generatePdfBlob(id);
        if (!mounted) return;
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (err) {
        console.error('PDF generation failed', err);
        if (mounted) setError(err?.message || 'Could not generate PDF');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [show, elementId, useTemplate]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    const fileName = invoiceTitle 
      ? `Invoice ${invoiceNo} - ${invoiceTitle}.pdf`
      : `Invoice ${invoiceNo}.pdf`;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered contentClassName="invoice-pdf-preview-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {invoiceTitle ? `${invoiceNo} - ${invoiceTitle}` : invoiceNo}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: 300 }}>
        {useTemplate && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            {/* Render template inside preview so html2canvas can capture it */}
            <div ref={templateRef} id="invoice-template-root-wrap">
              <InvoicePdfTemplate 
                id="invoice-template-root"
                invoiceNo={invoiceData.invoiceNo || invoiceNo || '-'}
                invoiceDate={invoiceData.invoiceDate || '-'}
                subtotal={invoiceData.subtotal || 0}
                gstAmount={invoiceData.gstAmount || 0}
                total={invoiceData.total || 0}
                dueDate={invoiceData.dueDate || '-'}
                clientName={invoiceData.clientName || ''}
                contactName={invoiceData.contactName || ''}
                clientAddress={invoiceData.clientAddress || ''}
                invoiceTitle={invoiceData.invoiceTitle || ''}
                description={invoiceData.description || ''}
              />
            </div>
          </div>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <Spinner animation="border" />
            <span className="ms-3">Generating PDF...</span>
          </div>
        )}
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}
        {!loading && !error && blobUrl && (
          <div className="d-flex justify-content-end gap-3 mt-3">
            <button className="btn btn-secondary btn-lg" onClick={onClose}>Close</button>
            <button className="btn btn-primary btn-lg" onClick={handleDownload}>Download PDF</button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
